import {
    Avatar,
    Box,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormHelperText,
    Grid,
    IconButton,
    LinearProgress,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Paper,
    Stack,
    Switch,
    TextField,
    Tooltip,
    Typography,
    styled,
} from '@mui/material';
import DOMPurify from 'dompurify';
import parse from 'html-react-parser';
import { useTranslation } from 'react-i18next';
import { blue } from '@mui/material/colors';
import { SCUIButton } from '../../../../atomic/atoms';
import {
    Add,
    Close,
    CloudDownload,
    CloudUploadOutlined,
    ConfirmationNumber,
    Delete,
    Edit,
    ErrorOutline,
    MoreHoriz,
    Refresh,
} from '@mui/icons-material';
import '../../CampaignManagement.scss';
import { StepCouponServiceComponent, StepCouponTimerComponent } from '../../services';
import { emptyCampaign, excelLogo } from '../../../../assets/images';
import React, { FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
    ICouponItem,
    ICouponValidations,
    ICouponModalProps,
    ICouponValidationRules,
} from '../../types/interfaces/CampaingTypes';
import { useDropzone } from 'react-dropzone';
import { CampaignManagementCommand } from '../../../../services/command';
import AuthService from '../../../../services/auth/Auth.services';
import Helper from '../../../../utils/helper';
import jwt_decode, { JwtPayload } from 'jwt-decode';
import {
    CampaignFileTypes,
    CampaignStatus,
    CouponExecutionType,
    CouponFormInput,
    CouponGenerationType,
    CouponGenerationTypes,
    CouponStatus,
} from '../../../../types/enums/campaign-enums';
import { useSelector } from 'react-redux';

export const SCUICampaignCouponStep = ({ ...props }) => {
    const { t } = useTranslation();

    const [userName, setUserName] = React.useState('');

    const regionStore = useSelector((state: any) => state.region);

    useEffect(() => {
        const getUserInfo = async () => {
            try {
                const authService = new AuthService();
                const userInfo = await authService.getUserInfo();
                if (!userInfo) {
                    console.error('User info bulunamadı');
                    return;
                }
                if (!Helper.isNil(userInfo)) {
                    const decodedToken: any = jwt_decode<JwtPayload>(userInfo.access_token);
                    setUserName(decodedToken.preferred_username.toLowerCase());
                }
            } catch (error) {
                console.error('Error getting user info:', error);
            }
        };

        getUserInfo();
    }, []);

    const {
        campaignDetail,
        stepperOnClickReset,
        saveCampaignForm,
        coupons,
        initialCouponForm,
        setInitialCouponForm,
        formData,
        setFormData,
        useCouponCampaign,
        useCouponCampaignDisabled,
        InputPermissons,
        ContinueButtonDisabled,
        modalInfo,
        useCouponOnChange,
        perCustomerCouponOnInput,
        getStatus,
        getExecutionStatus,
        getCharacterSet,
        downloadCouponFile,
        couponModalOpen,
        couponModalClose,
        couponModalSave,
        getCoupons,
        saveForm,
        getCouponCharacterSetTypesByRegion,
    } = StepCouponServiceComponent({ ...props });

    const filteredCharacterSetTypes = useMemo(() => {
        return getCouponCharacterSetTypesByRegion();
    }, [regionStore?.region]);

    const Item = styled(Card)(({ theme }) => ({
        ...theme.typography.body2,
        backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
        padding: theme.spacing(1),
        color: theme.palette.text.secondary,
    }));

    const EmptyItem = () => {
        return (
            <Item
                sx={{
                    my: 1,
                    p: 2,
                }}
                style={{ textAlign: 'center' }}
            >
                <img src={emptyCampaign} style={{ maxHeight: '250px' }} />
                <Typography style={{ margin: '20px' }}>
                    {t('campaignManagement.save.step.coupon.noRecordMessage')}
                </Typography>
            </Item>
        );
    };

    //First Input Fields on List
    const CouponCreateInputs = () => {
        const [formValue, setFormValue] = useState<any>({
            perCustomerCouponCount: formData.perCustomerCouponCount,
            couponType: formData.couponType,
        });

        const [errors, setErrors] = useState<{ [key: string]: string }>({});

        const validations: ICouponValidations = {
            perCustomerCouponCount: { required: false, min: 1, max: 999 },
            couponType: { required: true },
        };

        const [formCanBeChecked, setFormCanBeChecked] = useState<boolean>(false);

        useEffect(() => {
            if (!formCanBeChecked) return;

            validate();
        }, [formValue, formCanBeChecked]);

        const validateField = (_: string, value: string, rules: ICouponValidationRules): string | null => {
            if (rules.required && !value) {
                return t('campaignManagement.save.step.coupon.form.validationMessage.required');
            }

            if (rules.min !== undefined && (parseInt(value) || 0) < rules.min) {
                return t('campaignManagement.save.step.coupon.form.validationMessage.min').replace(
                    '{{min}}',
                    `${rules.min}`
                );
            }

            if (rules.max !== undefined && (parseInt(value) || 0) > rules.max) {
                return t('campaignManagement.save.step.coupon.form.validationMessage.max').replace(
                    '{{max}}',
                    `${rules.max}`
                );
            }

            return null;
        };

        const validate = () => {
            const newErrors: { [key: string]: string } = {};

            for (const fieldName in validations) {
                const fieldError = validateField(fieldName, formValue[fieldName], validations[fieldName]);
                if (fieldError) {
                    newErrors[fieldName] = fieldError;
                } else {
                    delete newErrors[fieldName];
                }
            }

            setErrors(newErrors);
            return Object.keys(newErrors).length == 0;
        };

        const handleChange = (e: any) => {
            const { name, value } = e.target;
            setFormValue((prevData: any) => ({ ...prevData, [name]: value }));
        };

        const handleSubmit = (e: any) => {
            e.preventDefault();

            setFormCanBeChecked(true);

            let isValid = validate();
            if (isValid) {
                couponModalOpen();
                setInitialCouponForm({
                    ...initialCouponForm,
                    perCustomerCouponCount: formValue.perCustomerCouponCount,
                    couponType: formValue.couponType,
                });
                setFormData({
                    ...formData,
                    perCustomerCouponCount: formValue.perCustomerCouponCount,
                    couponType: formValue.couponType,
                });
            }
        };

        return (
            <form>
                <Grid container>
                    <Grid
                        style={{ display: 'flex', alignItems: 'center' }}
                        size={{
                            xs: 12,
                            sm: 12,
                            md: 6,
                            lg: 6,
                        }}
                    >
                        <FormControl component="fieldset">
                            <FormGroup aria-label="position" row>
                                <FormControlLabel
                                    value={useCouponCampaign}
                                    control={
                                        <Switch
                                            color="primary"
                                            checked={useCouponCampaign}
                                            disabled={useCouponCampaignDisabled}
                                        />
                                    }
                                    label={t('campaignManagement.save.step.coupon.couponCampaign')}
                                    labelPlacement="start"
                                    style={{ margin: '0' }}
                                    onChange={useCouponOnChange}
                                />
                            </FormGroup>
                        </FormControl>
                    </Grid>
                    <Grid
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                        }}
                        size={{
                            xs: 12,
                            sm: 12,
                            md: 6,
                            lg: 6,
                        }}
                    >
                        <FormControl sx={{ m: 1.5, minWidth: 120 }}>
                            <TextField
                                required
                                select
                                id="couponType"
                                name="couponType"
                                label={t('campaignManagement.save.step.coupon.form.couponType.title')}
                                variant="outlined"
                                size="small"
                                disabled={!useCouponCampaign || coupons.length > 0}
                                slotProps={{
                                    select: {
                                        label: '',
                                        multiple: false,
                                        value: useCouponCampaign ? formValue.couponType : 0,
                                    },
                                    inputLabel: {
                                        shrink: true,
                                    },
                                }}
                                onChange={handleChange}
                                style={{ height: '3.5rem' }}
                                error={errors.couponType != undefined}
                                helperText={errors.couponType}
                            >
                                <MenuItem value={0} key={0}>
                                    {t('campaignManagement.save.step.coupon.select')}
                                </MenuItem>
                                {CouponGenerationTypes.length > 0 &&
                                    CouponGenerationTypes.map((item: any) => {
                                        return (
                                            <MenuItem value={item.id} key={item.id}>
                                                {t(item.name)}
                                            </MenuItem>
                                        );
                                    })}
                            </TextField>
                        </FormControl>

                        <FormControl sx={{ m: 1.5, minWidth: 120 }}>
                            <TextField
                                required
                                id={CouponFormInput.PerCustomerCouponCount}
                                name={CouponFormInput.PerCustomerCouponCount}
                                label={t('campaignManagement.save.step.coupon.form.perCustomerCouponCount.title')}
                                variant="outlined"
                                size="small"
                                type="number"
                                value={useCouponCampaign ? formValue.perCustomerCouponCount : ''}
                                disabled={!useCouponCampaign || coupons.length > 0}
                                slotProps={{
                                    inputLabel: {
                                        shrink: true,
                                    },
                                }}
                                onInput={perCustomerCouponOnInput}
                                onChange={handleChange}
                                style={{ height: '3.5rem' }}
                                error={errors.perCustomerCouponCount != undefined}
                                helperText={errors.perCustomerCouponCount}
                            />
                        </FormControl>

                        <Tooltip
                            title={t('campaignManagement.save.step.coupon.form.perCustomerCouponCount.alertMessage')}
                        >
                            <Typography>
                                <ErrorOutline style={{ fontSize: 24, color: '#0d6efd' }} />
                            </Typography>
                        </Tooltip>

                        <FormControl sx={{ m: 1, minWidth: 80, mr: 0 }}>
                            <SCUIButton
                                text={t('campaignManagement.save.step.coupon.createButton')}
                                type="primary"
                                sizeType="fixed"
                                buttonType="button"
                                size={'s'}
                                showStartIcon={true}
                                startIcon={<Add style={{ color: 'white' }} />}
                                disabled={!useCouponCampaign}
                                onClick={() => {
                                    handleSubmit(manualEvent);
                                }}
                            />
                        </FormControl>
                    </Grid>
                </Grid>
            </form>
        );
    };

    //List Items

    const GenericCouponItem: FC<ICouponItem> = memo(({ item }) => {
        const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

        const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
            setAnchorEl(event.currentTarget);
        };

        const handleMenuClose = () => {
            setAnchorEl(null);
        };

        let couponName = item.couponName;
        let status: any = getStatus(item.status);
        let executionStatus: any = getExecutionStatus(item.executionStatus, item.message);
        let percentLoadedValue = item.executionStatus == CouponExecutionType.Completed ? 100 : 0;

        // let couponType = CouponGenerationType[CouponGenerationType.Unique];
        let couponType = t(CouponGenerationTypes.find((x: any) => x.id == CouponGenerationType.Generic)?.name ?? '');
        let couponCode = item.couponCode;
        // Use a stable number format with dots as thousand separators
        const numberFormatter = new Intl.NumberFormat('tr-TR');
        let totalCouponCount = numberFormatter.format(item.totalCouponCount ?? 0);

        return (
            <Item
                sx={{
                    my: 1,
                    p: 2,
                }}
                variant="outlined"
                style={{ border: '1px solid #ebebeb' }}
            >
                <Grid container>
                    <Grid
                        size={{
                            xs: 12,
                            sm: 12,
                            md: 12,
                            lg: 12,
                        }}
                    >
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <span style={{ fontSize: '16px' }}>
                                    {t('campaignManagement.save.step.coupon.item.couponName') + ' :'}
                                    <span style={{ fontWeight: '800', margin: '0 5px' }}>{couponName}</span>
                                </span>
                                <span style={{ padding: '0 8px' }}>
                                    <Chip label={t(status.text)} size="small" color={status.color} />
                                </span>
                            </div>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    minWidth: '350px',
                                }}
                            >
                                <Box sx={{ width: '100%', textAlign: 'center' }}>
                                    <Tooltip title={executionStatus.message}>
                                        <Chip
                                            label={t(executionStatus.text)}
                                            size="small"
                                            color={executionStatus.color}
                                            style={{ color: executionStatus.textColor }}
                                        />
                                    </Tooltip>
                                </Box>
                                <Box sx={{ width: '100%', mr: 1 }}>
                                    <Tooltip title={`% ${percentLoadedValue}`}>
                                        <LinearProgress
                                            variant="determinate"
                                            value={percentLoadedValue}
                                            color={executionStatus.color}
                                        />
                                    </Tooltip>
                                </Box>
                                <Box sx={{ width: '50%', mr: 1, textAlign: 'end' }}>
                                    <IconButton
                                        aria-controls={anchorEl ? 'edit-menu' : undefined}
                                        aria-haspopup="true"
                                        aria-expanded={anchorEl ? 'true' : undefined}
                                        onClick={handleMenuOpen}
                                    >
                                        <MoreHoriz sx={{ fontSize: 24 }} />
                                    </IconButton>
                                    <Menu
                                        id="edit-menu"
                                        anchorEl={anchorEl}
                                        open={Boolean(anchorEl)}
                                        onClose={handleMenuClose}
                                        slotProps={{
                                            list: {
                                                'aria-labelledby': 'basic-button',
                                            },
                                        }}
                                        anchorOrigin={{
                                            vertical: 'bottom',
                                            horizontal: 'left',
                                        }}
                                        transformOrigin={{
                                            vertical: 'top',
                                            horizontal: 'left',
                                        }}
                                    >
                                        <MenuItem onClick={() => couponModalOpen(item.id)}>
                                            <ListItemIcon>
                                                <Edit fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText>
                                                {t('campaignManagement.save.step.coupon.item.action.editButton')}
                                            </ListItemText>
                                        </MenuItem>
                                    </Menu>
                                </Box>
                            </Box>
                        </Stack>
                    </Grid>
                </Grid>
                <Divider style={{ maxWidth: '100%', margin: '4px 0' }} />
                <Grid container>
                    <Grid
                        size={{
                            xs: 12,
                            sm: 12,
                            md: 12,
                            lg: 12,
                        }}
                    >
                        <Stack
                            spacing={2}
                            direction="row"
                            alignItems="center"
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                width: '100%',
                            }}
                        >
                            <div style={{ width: '20%' }}>
                                <Typography>{t('campaignManagement.save.step.coupon.item.couponType')}</Typography>
                                <Typography style={{ fontWeight: 'bold' }}>{couponType}</Typography>
                            </div>
                            <div style={{ width: '20%' }}>
                                <Typography align="left">
                                    {t('campaignManagement.save.step.coupon.item.couponCode')}
                                </Typography>
                                <Typography align="left" style={{ fontWeight: 'bold' }}>
                                    {couponCode}
                                </Typography>
                            </div>
                            <div style={{ width: '20%' }}>
                                <Typography align="left">
                                    {t('campaignManagement.save.step.coupon.item.totalUsageCount')}
                                </Typography>
                                <Typography align="left" style={{ fontWeight: 'bold' }}>
                                    {totalCouponCount}
                                </Typography>
                            </div>
                        </Stack>
                    </Grid>
                </Grid>
            </Item>
        );
    });

    const UniqueCouponItem: FC<ICouponItem> = memo(({ item }) => {
        const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

        const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
            setAnchorEl(event.currentTarget);
        };

        const handleMenuClose = () => {
            setAnchorEl(null);
        };

        let couponName = item.couponName;
        let status: any = getStatus(item.status);
        let executionStatus: any = getExecutionStatus(item.executionStatus, item.message);
        let percentLoadedValue =
            item.totalCouponCount > 0
                ? parseInt(((item.loadedCouponCount / item.totalCouponCount) * 100).toFixed(0))
                : 0;

        let couponType = t(CouponGenerationTypes.find((x: any) => x.id == CouponGenerationType.Unique)?.name ?? '');
        let characterSet = getCharacterSet(item.characterSet);
        // Use a stable number format with dots as thousand separators
        const numberFormatter = new Intl.NumberFormat('tr-TR');
        let totalCouponCount = numberFormatter.format(item.totalCouponCount ?? 0);

        let loadedCount = numberFormatter.format(item.loadedCouponCount ?? 0);
        let downloadButtonLabel = '';
        let downloadButtonTooltipLabel = '';
        if (item.userFileName != undefined && item.userFileName.length > 0) {
            downloadButtonLabel =
                item.userFileName.length <= 15 ? item.userFileName : item.userFileName.slice(0, 15) + '...';
            downloadButtonTooltipLabel = item.userFileName;
        } else {
            downloadButtonLabel = t(
                'campaignManagement.save.step.rulesAndEarnings.rule.item.conditionItem.row.downloadFile'
            );
            downloadButtonTooltipLabel = t(
                'campaignManagement.save.step.rulesAndEarnings.rule.item.conditionItem.row.downloadFile'
            );
        }

        return (
            <Item
                sx={{
                    my: 1,
                    p: 2,
                }}
                variant="outlined"
                style={{ border: '1px solid #ebebeb' }}
            >
                <Grid container>
                    <Grid
                        size={{
                            xs: 12,
                            sm: 12,
                            md: 12,
                            lg: 12,
                        }}
                    >
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <span style={{ fontSize: '16px' }}>
                                    {t('campaignManagement.save.step.coupon.item.couponName') + ' :'}
                                    <span style={{ fontWeight: '800', margin: '0 5px' }}>{couponName}</span>
                                </span>
                                <span style={{ padding: '0 8px' }}>
                                    <Chip label={t(status.text)} size="small" color={status.color} />
                                </span>
                            </Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    minWidth: '350px',
                                }}
                            >
                                <Box sx={{ width: '100%', textAlign: 'center' }}>
                                    <Tooltip title={executionStatus.message}>
                                        <Chip
                                            label={t(executionStatus.text)}
                                            size="small"
                                            color={executionStatus.color}
                                            style={{ color: executionStatus.textColor }}
                                        />
                                    </Tooltip>
                                </Box>
                                <Box sx={{ width: '100%', mr: 1 }}>
                                    <Tooltip title={`% ${percentLoadedValue}`}>
                                        <LinearProgress
                                            variant="determinate"
                                            value={percentLoadedValue}
                                            color={executionStatus.color}
                                        />
                                    </Tooltip>
                                </Box>
                                <Box sx={{ width: '50%', mr: 1, textAlign: 'end' }}>
                                    <IconButton
                                        aria-controls={anchorEl ? 'basic-menu' : undefined}
                                        aria-haspopup="true"
                                        aria-expanded={anchorEl ? 'true' : undefined}
                                        onClick={handleMenuOpen}
                                    >
                                        <MoreHoriz sx={{ fontSize: 24 }} />
                                    </IconButton>
                                    <Paper style={{ maxWidth: '100%' }}>
                                        <Menu
                                            id="edit-menu"
                                            anchorEl={anchorEl}
                                            open={Boolean(anchorEl)}
                                            onClose={handleMenuClose}
                                            slotProps={{
                                                list: {
                                                    'aria-labelledby': 'basic-button',
                                                },
                                            }}
                                        >
                                            <MenuItem onClick={() => couponModalOpen(item.id)}>
                                                <ListItemIcon>
                                                    <Edit fontSize="small" />
                                                </ListItemIcon>
                                                <ListItemText>
                                                    {t('campaignManagement.save.step.coupon.item.action.editButton')}
                                                </ListItemText>
                                            </MenuItem>
                                        </Menu>
                                    </Paper>
                                </Box>
                            </Box>
                        </Stack>
                    </Grid>
                </Grid>
                <Divider style={{ width: '100%', margin: '4px 0' }} />
                <Grid container>
                    <Grid
                        size={{
                            xs: 12,
                            sm: 12,
                            md: 12,
                            lg: 12,
                        }}
                    >
                        <Stack
                            spacing={2}
                            direction="row"
                            alignItems="center"
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                width: '100%',
                            }}
                        >
                            <div style={{ width: '20%' }}>
                                <Typography>{t('campaignManagement.save.step.coupon.item.couponType')}</Typography>
                                <Typography style={{ fontWeight: 'bold' }}>{couponType}</Typography>
                            </div>
                            <div style={{ width: '20%' }}>
                                <Typography align="left">
                                    {t('campaignManagement.save.step.coupon.item.characterSet')}
                                </Typography>
                                <Typography align="left" style={{ fontWeight: 'bold' }}>
                                    {characterSet}
                                </Typography>
                            </div>
                            <div style={{ width: '20%' }}>
                                <Typography align="left">
                                    {t('campaignManagement.save.step.coupon.item.totalUsageCount')}
                                </Typography>
                                <Typography align="left" style={{ fontWeight: 'bold' }}>
                                    {totalCouponCount}
                                </Typography>
                            </div>
                            <div style={{ width: '20%' }}>
                                <Typography align="left">
                                    {t('campaignManagement.save.step.coupon.item.loadedCount')}
                                </Typography>
                                <Typography align="left" style={{ fontWeight: 'bold' }}>
                                    {loadedCount}
                                </Typography>
                            </div>
                        </Stack>
                    </Grid>
                </Grid>
                {item.executionStatus == CouponExecutionType.Completed && (
                    <>
                        <Divider style={{ width: '100%', margin: '4px 0' }} />
                        <Grid container>
                            <Grid
                                style={{ paddingLeft: '0' }}
                                size={{
                                    xs: 12,
                                    sm: 12,
                                    md: 12,
                                    lg: 12,
                                }}
                            >
                                <Tooltip title={downloadButtonTooltipLabel}>
                                    <Chip
                                        label={downloadButtonLabel}
                                        size="small"
                                        variant="outlined"
                                        avatar={<img src={excelLogo} />}
                                        style={{ marginRight: '8px' }}
                                        onClick={() => downloadCouponFile(item.id)}
                                        onDelete={() => downloadCouponFile(item.id)}
                                        deleteIcon={<CloudDownload />}
                                    />
                                </Tooltip>
                            </Grid>
                        </Grid>
                    </>
                )}
            </Item>
        );
    });

    const PersonalCouponItem: FC<ICouponItem> = memo(({ item }) => {
        const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

        const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
            setAnchorEl(event.currentTarget);
        };

        const handleMenuClose = () => {
            setAnchorEl(null);
        };

        let couponName = item.couponName;
        let status: any = getStatus(item.status);
        let executionStatus: any = getExecutionStatus(item.executionStatus, item.message);
        let percentLoadedValue =
            item.totalCouponCount > 0
                ? parseInt(((item.loadedCouponCount / item.totalCouponCount) * 100).toFixed(0))
                : 0;

        let couponType = t(CouponGenerationTypes.find((x: any) => x.id == CouponGenerationType.Personal)?.name ?? '');
        let characterSet = getCharacterSet(item.characterSet);
        // Use a stable number format with dots as thousand separators
        const numberFormatter = new Intl.NumberFormat('tr-TR');
        let totalCouponCount = numberFormatter.format(item.totalCouponCount ?? 0);

        let loadedCount = numberFormatter.format(item.loadedCouponCount ?? 0);
        let downloadButtonLabel = '';
        let downloadButtonTooltipLabel = '';
        if (item.userFileName != undefined && item.userFileName.length > 0) {
            downloadButtonLabel =
                item.userFileName.length <= 15 ? item.userFileName : item.userFileName.slice(0, 15) + '...';
            downloadButtonTooltipLabel = item.userFileName;
        } else {
            downloadButtonLabel = t(
                'campaignManagement.save.step.rulesAndEarnings.rule.item.conditionItem.row.downloadFile'
            );
            downloadButtonTooltipLabel = t(
                'campaignManagement.save.step.rulesAndEarnings.rule.item.conditionItem.row.downloadFile'
            );
        }

        return (
            <Item
                sx={{
                    my: 1,
                    p: 2,
                }}
                variant="outlined"
                style={{ border: '1px solid #ebebeb' }}
            >
                <Grid container>
                    <Grid
                        size={{
                            xs: 12,
                            sm: 12,
                            md: 12,
                            lg: 12,
                        }}
                    >
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <span style={{ fontSize: '16px' }}>
                                    {t('campaignManagement.save.step.coupon.item.couponName') + ' :'}
                                    <span style={{ fontWeight: '800', margin: '0 5px' }}>{couponName}</span>
                                </span>
                                <span style={{ padding: '0 8px' }}>
                                    <Chip label={t(status.text)} size="small" color={status.color} />
                                </span>
                            </div>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    minWidth: '350px',
                                }}
                            >
                                <Box sx={{ width: '100%', textAlign: 'center' }}>
                                    <Tooltip title={executionStatus.message}>
                                        <Chip
                                            label={t(executionStatus.text)}
                                            size="small"
                                            color={executionStatus.color}
                                            style={{ color: executionStatus.textColor }}
                                        />
                                    </Tooltip>
                                </Box>
                                <Box sx={{ width: '100%', mr: 1 }}>
                                    <Tooltip title={`% ${percentLoadedValue}`}>
                                        <LinearProgress
                                            variant="determinate"
                                            value={percentLoadedValue}
                                            color={executionStatus.color}
                                        />
                                    </Tooltip>
                                </Box>
                                <Box sx={{ width: '50%', mr: 1, textAlign: 'end' }}>
                                    <IconButton
                                        aria-controls={anchorEl ? 'basic-menu' : undefined}
                                        aria-haspopup="true"
                                        aria-expanded={anchorEl ? 'true' : undefined}
                                        onClick={handleMenuOpen}
                                    >
                                        <MoreHoriz sx={{ fontSize: 24 }} />
                                    </IconButton>
                                    <Paper sx={{ maxWidth: '100%' }}>
                                        <Menu
                                            id="edit-menu"
                                            anchorEl={anchorEl}
                                            open={Boolean(anchorEl)}
                                            onClose={handleMenuClose}
                                            slotProps={{
                                                list: {
                                                    'aria-labelledby': 'basic-button',
                                                },
                                            }}
                                        >
                                            <MenuItem onClick={() => couponModalOpen(item.id)}>
                                                <ListItemIcon>
                                                    <Edit fontSize="small" />
                                                </ListItemIcon>
                                                <ListItemText>
                                                    {t('campaignManagement.save.step.coupon.item.action.editButton')}
                                                </ListItemText>
                                            </MenuItem>
                                        </Menu>
                                    </Paper>
                                </Box>
                            </Box>
                        </Stack>
                    </Grid>
                </Grid>
                <Divider style={{ width: '100%', margin: '4px 0' }} />
                <Grid container>
                    <Grid
                        size={{
                            xs: 12,
                            sm: 12,
                            md: 12,
                            lg: 12,
                        }}
                    >
                        <Stack
                            spacing={2}
                            direction="row"
                            alignItems="center"
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                width: '100%',
                            }}
                        >
                            <div style={{ width: '20%' }}>
                                <Typography>{t('campaignManagement.save.step.coupon.item.couponType')}</Typography>
                                <Typography style={{ fontWeight: 'bold' }}>{couponType}</Typography>
                            </div>
                            <div style={{ width: '20%' }}>
                                <Typography align="left">
                                    {t('campaignManagement.save.step.coupon.item.characterSet')}
                                </Typography>
                                <Typography align="left" style={{ fontWeight: 'bold' }}>
                                    {characterSet}
                                </Typography>
                            </div>
                            <div style={{ width: '20%' }}>
                                <Typography align="left">
                                    {t('campaignManagement.save.step.coupon.item.totalUsageCount')}
                                </Typography>
                                <Typography align="left" style={{ fontWeight: 'bold' }}>
                                    {totalCouponCount}
                                </Typography>
                            </div>
                            <div style={{ width: '20%' }}>
                                <Typography align="left">
                                    {t('campaignManagement.save.step.coupon.item.loadedCount')}
                                </Typography>
                                <Typography align="left" style={{ fontWeight: 'bold' }}>
                                    {loadedCount}
                                </Typography>
                            </div>
                        </Stack>
                    </Grid>
                </Grid>
                {item.executionStatus == CouponExecutionType.Completed && (
                    <>
                        <Divider style={{ width: '100%', margin: '4px 0' }} />
                        <Grid
                            style={{ paddingLeft: '0' }}
                            size={{
                                xs: 12,
                                sm: 12,
                                md: 12,
                                lg: 12,
                            }}
                        >
                            <Tooltip title={downloadButtonTooltipLabel}>
                                <Chip
                                    label={downloadButtonLabel}
                                    size="small"
                                    variant="outlined"
                                    avatar={<img src={excelLogo} />}
                                    style={{ marginRight: '8px' }}
                                    onClick={() => downloadCouponFile(item.id)}
                                    onDelete={() => downloadCouponFile(item.id)}
                                    deleteIcon={<CloudDownload />}
                                />
                            </Tooltip>
                        </Grid>
                    </>
                )}
            </Item>
        );
    });

    const ExcelCouponItem: FC<ICouponItem> = memo(({ item }) => {
        const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

        const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
            setAnchorEl(event.currentTarget);
        };

        const handleMenuClose = () => {
            setAnchorEl(null);
        };

        let couponName = item.couponName;
        let status: any = getStatus(item.status);
        let executionStatus: any = getExecutionStatus(item.executionStatus, item.message);
        let percentLoadedValue =
            item.totalCouponCount > 0
                ? parseInt(((item.loadedCouponCount / item.totalCouponCount) * 100).toFixed(0))
                : 0;

        let couponType = t(CouponGenerationTypes.find((x: any) => x.id == CouponGenerationType.Excel)?.name ?? '');
        let characterSet = getCharacterSet(item.characterSet);
        // Use a stable number format with dots as thousand separators
        const numberFormatter = new Intl.NumberFormat('tr-TR');
        let totalCouponCount = numberFormatter.format(item.totalCouponCount ?? 0);

        let loadedCount = numberFormatter.format(item.loadedCouponCount ?? 0);

        let downloadButtonLabel = '';
        let downloadButtonTooltipLabel = '';
        if (item.userFileName != undefined && item.userFileName.length > 0) {
            downloadButtonLabel =
                item.userFileName.length <= 15 ? item.userFileName : item.userFileName.slice(0, 15) + '...';
            downloadButtonTooltipLabel = item.userFileName;
        } else {
            downloadButtonLabel = t(
                'campaignManagement.save.step.rulesAndEarnings.rule.item.conditionItem.row.downloadFile'
            );
            downloadButtonTooltipLabel = t(
                'campaignManagement.save.step.rulesAndEarnings.rule.item.conditionItem.row.downloadFile'
            );
        }

        return (
            <Item
                sx={{
                    my: 1,
                    p: 2,
                }}
                variant="outlined"
                style={{ border: '1px solid #ebebeb' }}
            >
                <Grid container>
                    <Grid
                        size={{
                            xs: 12,
                            sm: 12,
                            md: 12,
                            lg: 12,
                        }}
                    >
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <span style={{ fontSize: '16px' }}>
                                    {t('campaignManagement.save.step.coupon.item.couponName') + ' :'}
                                    <span style={{ fontWeight: '800', margin: '0 5px' }}>{couponName}</span>
                                </span>
                                <span style={{ padding: '0 8px' }}>
                                    <Chip label={t(status.text)} size="small" color={status.color} />
                                </span>
                            </div>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    minWidth: '350px',
                                }}
                            >
                                <Box sx={{ width: '100%', textAlign: 'center' }}>
                                    <Tooltip title={executionStatus.message}>
                                        <Chip
                                            label={t(executionStatus.text)}
                                            size="small"
                                            color={executionStatus.color}
                                            style={{ color: executionStatus.textColor }}
                                        />
                                    </Tooltip>
                                </Box>
                                <Box sx={{ width: '100%', mr: 1 }}>
                                    <Tooltip title={`% ${percentLoadedValue}`}>
                                        <LinearProgress
                                            variant="determinate"
                                            value={percentLoadedValue}
                                            color={executionStatus.color}
                                        />
                                    </Tooltip>
                                </Box>
                                <Box sx={{ width: '50%', mr: 1, textAlign: 'end' }}>
                                    <IconButton
                                        aria-controls={anchorEl ? 'basic-menu' : undefined}
                                        aria-haspopup="true"
                                        aria-expanded={anchorEl ? 'true' : undefined}
                                        onClick={handleMenuOpen}
                                    >
                                        <MoreHoriz sx={{ fontSize: 24 }} />
                                    </IconButton>
                                    <Paper sx={{ maxWidth: '100%' }}>
                                        <Menu
                                            id="edit-menu"
                                            anchorEl={anchorEl}
                                            open={Boolean(anchorEl)}
                                            onClose={handleMenuClose}
                                            slotProps={{
                                                list: {
                                                    'aria-labelledby': 'basic-button',
                                                },
                                            }}
                                        >
                                            <MenuItem onClick={() => couponModalOpen(item.id)}>
                                                <ListItemIcon>
                                                    <Edit fontSize="small" />
                                                </ListItemIcon>
                                                <ListItemText>
                                                    {t('campaignManagement.save.step.coupon.item.action.editButton')}
                                                </ListItemText>
                                            </MenuItem>
                                        </Menu>
                                    </Paper>
                                </Box>
                            </Box>
                        </Stack>
                    </Grid>
                </Grid>
                <Divider style={{ width: '100%', margin: '4px 0' }} />
                <Grid container>
                    <Grid
                        size={{
                            xs: 12,
                            sm: 12,
                            md: 12,
                            lg: 12,
                        }}
                    >
                        <Stack
                            spacing={2}
                            direction="row"
                            alignItems="center"
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                width: '100%',
                            }}
                        >
                            <div style={{ width: '20%' }}>
                                <Typography>{t('campaignManagement.save.step.coupon.item.couponType')}</Typography>
                                <Typography style={{ fontWeight: 'bold' }}>{couponType}</Typography>
                            </div>
                            <div style={{ width: '20%' }}>
                                <Typography align="left">
                                    {t('campaignManagement.save.step.coupon.item.characterSet')}
                                </Typography>
                                <Typography align="left" style={{ fontWeight: 'bold' }}>
                                    {characterSet}
                                </Typography>
                            </div>
                            <div style={{ width: '20%' }}>
                                <Typography align="left">
                                    {t('campaignManagement.save.step.coupon.item.totalUsageCount')}
                                </Typography>
                                <Typography align="left" style={{ fontWeight: 'bold' }}>
                                    {totalCouponCount}
                                </Typography>
                            </div>
                            <div style={{ width: '20%' }}>
                                <Typography align="left">
                                    {t('campaignManagement.save.step.coupon.item.loadedCount')}
                                </Typography>
                                <Typography align="left" style={{ fontWeight: 'bold' }}>
                                    {loadedCount}
                                </Typography>
                            </div>
                        </Stack>
                    </Grid>
                </Grid>
                {item.executionStatus == CouponExecutionType.Completed && (
                    <>
                        <Divider style={{ width: '100%', margin: '4px 0' }} />
                        <Grid
                            style={{ paddingLeft: '0' }}
                            size={{
                                xs: 12,
                                sm: 12,
                                md: 12,
                                lg: 12,
                            }}
                        >
                            <Tooltip title={downloadButtonTooltipLabel}>
                                <Chip
                                    label={downloadButtonLabel}
                                    size="small"
                                    variant="outlined"
                                    avatar={<img src={excelLogo} />}
                                    style={{ marginRight: '8px' }}
                                    onClick={() => downloadCouponFile(item.id)}
                                    onDelete={() => downloadCouponFile(item.id)}
                                    deleteIcon={<CloudDownload />}
                                />
                            </Tooltip>
                        </Grid>
                    </>
                )}
            </Item>
        );
    });

    //List

    const CouponList = memo(() => {
        return (
            <div style={{ margin: '16px 0' }}>
                <Stack spacing={2}>
                    {coupons &&
                        coupons.map((coupon: any, cIndex: number) => {
                            switch (coupon.generationType) {
                                case CouponGenerationType.Unique:
                                    return <UniqueCouponItem key={coupon.id || cIndex} item={coupon} />;
                                case CouponGenerationType.Generic:
                                    return <GenericCouponItem key={coupon.id || cIndex} item={coupon} />;
                                case CouponGenerationType.Personal:
                                    return <PersonalCouponItem key={coupon.id || cIndex} item={coupon} />;
                                case CouponGenerationType.Excel:
                                    return <ExcelCouponItem key={coupon.id || cIndex} item={coupon} />;
                                default:
                                    return null;
                            }
                        })}

                    {coupons && coupons.length == 0 && <EmptyItem />}
                    <StepCouponTimerComponent
                        {...props}
                        modalInfo={modalInfo}
                        coupons={coupons}
                        getCoupons={getCoupons}
                    />
                </Stack>
            </div>
        );
    });

    //Modal

    const CouponModal: FC<ICouponModalProps> = memo(({ isOpen, onClose }) => {
        const CouponForm: FC = memo(() => {
            const { editMode } = modalInfo;
            const { campaignRule } = campaignDetail;

            // Memoize initial form value to prevent unnecessary re-renders
            const initialFormValue = useMemo(
                () => ({
                    id: { value: formData.id, editable: false },
                    couponName: { value: formData.couponName, editable: campaignRule.status != CampaignStatus.Active },
                    couponCode: { value: formData.couponCode, editable: false },
                    characterSet: { value: formData.characterSet, editable: false },
                    prefix: { value: formData.prefix, editable: false },
                    postFix: { value: formData.postFix, editable: false },
                    totalCouponCount: {
                        value: formData.totalCouponCount,
                        editable: formData.couponType == CouponGenerationType.Generic,
                    },
                    status: { value: formData.status, editable: true },
                    userFileId: { value: formData.userFileId, editable: false },
                }),
                [formData, campaignRule.status]
            );

            const [formValue, setFormValue] = React.useState<any>(initialFormValue);

            const [errors, setErrors] = useState<{ [key: string]: string }>({});

            // Memoize validations to prevent unnecessary re-renders
            const validations = useMemo<ICouponValidations>(
                () => ({
                    couponName: {
                        required: true,
                        minLength: 5,
                        maxLength: 25,
                        show: [
                            CouponGenerationType.Unique,
                            CouponGenerationType.Generic,
                            CouponGenerationType.Personal,
                            CouponGenerationType.Excel,
                        ],
                    },
                    couponCode: {
                        required: true,
                        minLength: 5,
                        maxLength: 15,
                        spaceless: true,
                        show: [CouponGenerationType.Generic],
                    },
                    characterSet: {
                        required: true,
                        show: [CouponGenerationType.Unique, CouponGenerationType.Personal],
                    },
                    prefix: {
                        required: false,
                        maxLength: 5,
                        spaceless: true,
                        show: [CouponGenerationType.Unique, CouponGenerationType.Personal],
                    },
                    postFix: {
                        required: false,
                        maxLength: 5,
                        spaceless: true,
                        show: [CouponGenerationType.Unique, CouponGenerationType.Personal],
                    },
                    totalCouponCount: {
                        required: false,
                        min: 1,
                        max: 10000000,
                        show: [CouponGenerationType.Unique, CouponGenerationType.Generic],
                    },
                    userFileId: {
                        required: true,
                        show: [CouponGenerationType.Personal, CouponGenerationType.Excel],
                    },
                }),
                []
            );

            const [formCanBeChecked, setFormCanBeChecked] = useState<boolean>(false);

            const [selectedFile, setSelectedFile] = useState<any>({
                id: 0,
                fileObject: null,
                error: false,
            });

            const validateField = useCallback(
                (fieldName: string, value: string, rules: ICouponValidationRules): string | null => {
                    if (rules.required && !value) {
                        return t('campaignManagement.save.step.coupon.form.validationMessage.required');
                    }

                    if (rules.minLength !== undefined && value.length < rules.minLength) {
                        return t('campaignManagement.save.step.coupon.form.validationMessage.minLength').replace(
                            '{{minLength}}',
                            `${rules.minLength}`
                        );
                    }

                    if (rules.maxLength !== undefined && value.length > rules.maxLength) {
                        return t('campaignManagement.save.step.coupon.form.validationMessage.maxLength').replace(
                            '{{maxLength}}',
                            `${rules.maxLength}`
                        );
                    }

                    if (rules.min !== undefined && (parseInt(value) || 0) < rules.min) {
                        return t('campaignManagement.save.step.coupon.form.validationMessage.min').replace(
                            '{{min}}',
                            `${rules.min}`
                        );
                    }

                    if (rules.max !== undefined && (parseInt(value) || 0) > rules.max) {
                        return t('campaignManagement.save.step.coupon.form.validationMessage.max').replace(
                            '{{max}}',
                            `${rules.max}`
                        );
                    }

                    if (rules.spaceless !== undefined && /\s+/.test(value)) {
                        return t('campaignManagement.save.step.coupon.form.validationMessage.spaceless');
                    }

                    return null;
                },
                [t]
            );

            const validate = useCallback(() => {
                const newErrors: { [key: string]: string } = {};

                for (const fieldName in validations) {
                    const isShowProp = Object.prototype.hasOwnProperty.call(validations[fieldName], 'show');
                    const foundValidation = isShowProp
                        ? validations[fieldName].show?.some((x) => x == formData.couponType)
                        : true;

                    let fieldError = null;

                    if (foundValidation) {
                        fieldError = validateField(
                            fieldName,
                            formValue[fieldName]?.value || '',
                            validations[fieldName]
                        );
                    }

                    if (fieldError) {
                        newErrors[fieldName] = fieldError;
                    } else {
                        delete newErrors[fieldName];
                    }
                }

                setErrors(newErrors);
                return Object.keys(newErrors).length == 0;
            }, [formValue, validations, formData.couponType, validateField]);

            // Reset form value when modal opens or formData changes
            useEffect(() => {
                if (isOpen) {
                    setFormValue(initialFormValue);
                }
            }, [isOpen, initialFormValue]);

            useEffect(() => {
                if (!formCanBeChecked) return;

                validate();
            }, [formCanBeChecked, validate]);

            const handleChange = useCallback((e: any) => {
                let { name, value, checked } = e.target;

                if ([CouponFormInput.CouponCode, CouponFormInput.Prefix, CouponFormInput.Postfix].includes(name)) {
                    const allowedRegex = /[a-zA-Z0-9ğüşöçıİĞÜŞÖÇ؀-ۿ]/g;
                    const rawValue = value;

                    const cleanedValue = (rawValue?.match(allowedRegex) || []).join('');

                    setFormValue((prevFormValue: any) => ({
                        ...prevFormValue,
                        [name]: { ...prevFormValue[`${name}`], value: cleanedValue },
                    }));
                    return;
                }

                if (name == 'totalCouponCount') value = parseInt(value);
                if (name == 'status') value = checked ? CouponStatus.Active : CouponStatus.Passive;

                setFormValue((prevFormValue: any) => ({
                    ...prevFormValue,
                    [name]: { ...prevFormValue[`${name}`], value: value },
                }));
            }, []);

            const handleCouponAmountChange = useCallback(
                (e: any) => {
                    let { name, value } = e.target;

                    let rawValue = value?.replace(/\D/g, '');

                    if (rawValue > 10000000) return;

                    handleChange({
                        target: {
                            name: name,
                            value: rawValue,
                        },
                    });
                },
                [handleChange]
            );

            const formatNumberWithDots = useCallback((value: any) => {
                if (!value) return '';

                let cultureCode = 'tr-TR';
                try {
                    const selectedCountry = localStorage.getItem('selectedCountry');
                    if (selectedCountry) {
                        cultureCode = JSON.parse(selectedCountry)?.countryCode || 'tr-TR';
                    }
                } catch {
                    // Fallback to tr-TR
                    cultureCode = 'tr-TR';
                }

                return new Intl.NumberFormat(cultureCode).format(Number(value));
            }, []);

            const handleSubmit = useCallback(
                (e: any) => {
                    e.preventDefault();

                    setFormCanBeChecked(true);

                    let isValid = validate();
                    if (isValid) {
                        couponModalSave(formValue);
                    }
                },
                [validate, formValue, couponModalSave]
            );

            const visible = useCallback(
                (inputName: string) => {
                    return InputPermissons.find((x: any) => x.key == inputName)?.show.some(
                        (x: any) => x == formData.couponType
                    );
                },
                [InputPermissons, formData.couponType]
            );

            const editable = useCallback(
                (inputName: string) => {
                    let result: boolean = false;
                    if (!editMode) {
                        result = true;
                    } else if (editMode && formValue[inputName]?.editable) {
                        result = true;
                    }
                    return result;
                },
                [editMode, formValue]
            );

            const dropzoneStyles = {
                display: 'flex',
                border: '2px dashed',
                borderRadius: '4px',
                padding: '0 8px',
                height: '52px',
                cursor: 'pointer',
                '&:hover': {
                    backgroundColor: '#03a9f41f',
                },
            };

            const FileUpload = () => {
                const onDrop = useCallback(
                    async (acceptedFiles: File[]) => {
                        const file = acceptedFiles[0] ?? selectedFile.fileObject;

                        try {
                            if (file == null) return;

                            const formDataObject = new FormData();
                            formDataObject.append('file', file);
                            formDataObject.append('type', String(CampaignFileTypes.Coupon));
                            formDataObject.append('regionId', regionStore.region);
                            formDataObject.append('creatorUser', userName);

                            const response = await CampaignManagementCommand.FileUpload(formDataObject);

                            if (response.isSuccess) {
                                setSelectedFile({
                                    id: response.data.id,
                                    fileObject: file,
                                    error: false,
                                });

                                handleChange({
                                    target: { name: 'userFileId', value: response.data.id },
                                });

                                console.log('File(s) uploaded successfully.');
                            } else {
                                setSelectedFile({
                                    id: 0,
                                    fileObject: file,
                                    error: true,
                                });

                                handleChange({
                                    target: { name: 'userFileId', value: 0 },
                                });

                                console.error('File(s) upload failed.');
                            }
                        } catch (error) {
                            setSelectedFile({
                                id: 0,
                                fileObject: file,
                                error: true,
                            });

                            handleChange({
                                target: { name: 'userFileId', value: 0 },
                            });

                            console.error('File upload error:', error);
                        }
                    },
                    [selectedFile.fileObject, regionStore.region, userName, handleChange]
                );

                const { acceptedFiles, getRootProps, getInputProps, isDragActive } = useDropzone({
                    onDrop,
                    accept: ['.xls', '.xlsx'],
                    maxFiles: 1,
                });

                const onRemove = useCallback(() => {
                    setSelectedFile({
                        id: 0,
                        fileObject: null,
                        error: false,
                    });

                    handleChange({
                        target: { name: 'userFileId', value: 0 },
                    });
                }, [handleChange]);

                return (
                    <div className="container">
                        {selectedFile != null && selectedFile.fileObject == null && (
                            <>
                                <div
                                    {...getRootProps()}
                                    style={{
                                        ...dropzoneStyles,
                                        borderColor: errors.userFileId != undefined ? '#d32f2f' : '#0d6efd',
                                    }}
                                >
                                    <input id="userFileId" name="userFileId" {...getInputProps()} />
                                    <Stack direction="row" alignItems="center" spacing={2}>
                                        <CloudUploadOutlined style={{ height: '28px', width: '28px' }} />
                                        {isDragActive ? (
                                            <span style={{ width: '100%' }}>
                                                {t('campaignManagement.save.step.coupon.form.file.dragMessage')}
                                            </span>
                                        ) : (
                                            <span style={{ width: '100%' }}>
                                                {parse(
                                                    DOMPurify.sanitize(
                                                        t('campaignManagement.save.step.coupon.form.file.title')
                                                    )
                                                )}
                                            </span>
                                        )}
                                    </Stack>
                                </div>
                                {errors.userFileId && (
                                    <p
                                        style={{
                                            color: '#d32f2f',
                                            fontSize: 'small',
                                            margin: '4px 14px 0 14px',
                                        }}
                                    >
                                        {errors.userFileId}
                                    </p>
                                )}
                            </>
                        )}

                        {selectedFile != null && selectedFile.fileObject != null && (
                            <>
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={2}
                                    style={{
                                        border: '2px dashed',
                                        borderColor: selectedFile.error ? '#ff1b1c' : '#0d6efd',
                                        borderRadius: '4px',
                                        padding: '0 8px',
                                        backgroundColor: selectedFile.error ? '#ffe9e9' : '#f0f6ff',
                                        height: '52px',
                                    }}
                                >
                                    <img src={excelLogo} style={{ height: '28px', width: '28px' }} />
                                    <span style={{ width: '100%' }}>{selectedFile.fileObject.name}</span>
                                    {selectedFile.error && (
                                        <IconButton
                                            aria-label="retry"
                                            size="large"
                                            onClick={() => onDrop(acceptedFiles)}
                                            sx={{ m: '0!important' }}
                                        >
                                            <Refresh fontSize="large" />
                                        </IconButton>
                                    )}
                                    <IconButton
                                        aria-label="delete"
                                        size="large"
                                        onClick={() => onRemove()}
                                        sx={{ m: '0!important' }}
                                    >
                                        <Delete fontSize="large" />
                                    </IconButton>
                                </Stack>

                                {selectedFile.error && (
                                    <Typography variant="subtitle1" style={{ color: 'red' }}>
                                        {t('campaignManagement.save.step.coupon.form.file.errorMessage.anErrorOccured')}
                                    </Typography>
                                )}
                            </>
                        )}
                    </div>
                );
            };

            return (
                <>
                    <DialogContent dividers>
                        <Box sx={{ m: 2 }}>
                            <Grid container>
                                {visible(CouponFormInput.CouponName) && (
                                    <Grid
                                        style={{ paddingLeft: 0, margin: '8px' }}
                                        size={{
                                            xs: 12,
                                            sm: 12,
                                            md: 12,
                                            lg: 12,
                                        }}
                                    >
                                        <FormControl fullWidth>
                                            <TextField
                                                required
                                                id={CouponFormInput.CouponName}
                                                name={CouponFormInput.CouponName}
                                                label={t('campaignManagement.save.step.coupon.form.couponName.title')}
                                                value={formValue.couponName.value}
                                                defaultValue={formValue.couponName.value}
                                                disabled={!editable(CouponFormInput.CouponName)}
                                                onChange={handleChange}
                                                error={errors.couponName != undefined}
                                                helperText={errors.couponName}
                                            />
                                        </FormControl>
                                    </Grid>
                                )}

                                {visible(CouponFormInput.CouponCode) && (
                                    <Grid
                                        style={{ paddingLeft: 0, margin: '8px' }}
                                        size={{
                                            xs: 12,
                                            sm: 12,
                                            md: 12,
                                            lg: 12,
                                        }}
                                    >
                                        <FormControl fullWidth>
                                            <TextField
                                                required
                                                id={CouponFormInput.CouponCode}
                                                name={CouponFormInput.CouponCode}
                                                label={t('campaignManagement.save.step.coupon.form.couponCode.title')}
                                                value={formValue?.couponCode?.value || ''}
                                                disabled={!editable(CouponFormInput.CouponCode)}
                                                onChange={handleChange}
                                                error={errors.couponCode != undefined}
                                                helperText={errors.couponCode}
                                            />
                                        </FormControl>
                                    </Grid>
                                )}

                                {visible(CouponFormInput.CharacterSet) && (
                                    <Grid
                                        style={{ paddingLeft: 0, margin: '8px' }}
                                        size={{
                                            xs: 12,
                                            sm: 12,
                                            md: 12,
                                            lg: 12,
                                        }}
                                    >
                                        <FormControl fullWidth>
                                            <TextField
                                                select
                                                required
                                                id={CouponFormInput.CharacterSet}
                                                name={CouponFormInput.CharacterSet}
                                                label={t('campaignManagement.save.step.coupon.form.characterSet.title')}
                                                slotProps={{
                                                    select: {
                                                        label: '',
                                                        multiple: false,
                                                        value: formValue.characterSet.value,
                                                        disabled: !editable(CouponFormInput.CharacterSet),
                                                    },
                                                }}
                                                onChange={handleChange}
                                                error={errors.characterSet != undefined}
                                                helperText={errors.characterSet}
                                            >
                                                <MenuItem value={0} key={0}>
                                                    {t('campaignManagement.save.step.coupon.select')}
                                                </MenuItem>
                                                {filteredCharacterSetTypes.length > 0 &&
                                                    filteredCharacterSetTypes.map((item: any) => {
                                                        return (
                                                            <MenuItem value={item.id} key={item.id}>
                                                                {item.name}
                                                            </MenuItem>
                                                        );
                                                    })}
                                            </TextField>
                                        </FormControl>
                                    </Grid>
                                )}

                                {visible(CouponFormInput.Prefix) && (
                                    <Grid
                                        style={{ padding: '0 8px', margin: '8px 0' }}
                                        size={{
                                            xs: 6,
                                            sm: 6,
                                            md: 6,
                                            lg: 6,
                                        }}
                                    >
                                        <FormControl fullWidth>
                                            <TextField
                                                id={CouponFormInput.Prefix}
                                                name={CouponFormInput.Prefix}
                                                label={t('campaignManagement.save.step.coupon.form.prefix.title')}
                                                value={formValue?.prefix?.value || ''}
                                                disabled={!editable(CouponFormInput.Prefix)}
                                                onChange={handleChange}
                                                error={errors.prefix != undefined}
                                                helperText={errors.prefix}
                                            />
                                        </FormControl>
                                    </Grid>
                                )}

                                {visible(CouponFormInput.Postfix) && (
                                    <Grid
                                        style={{ padding: '0 8px', margin: '8px 0' }}
                                        size={{
                                            xs: 6,
                                            sm: 6,
                                            md: 6,
                                            lg: 6,
                                        }}
                                    >
                                        <FormControl fullWidth>
                                            <TextField
                                                id={CouponFormInput.Postfix}
                                                name={CouponFormInput.Postfix}
                                                label={t('campaignManagement.save.step.coupon.form.postfix.title')}
                                                value={formValue?.postFix?.value || ''}
                                                disabled={!editable(CouponFormInput.Postfix)}
                                                onChange={handleChange}
                                                error={errors.postFix != undefined}
                                                helperText={errors.postFix}
                                            />
                                        </FormControl>
                                    </Grid>
                                )}

                                {visible(CouponFormInput.TotalCouponCount) && (
                                    <Grid
                                        style={{ paddingLeft: 0, margin: '8px' }}
                                        size={{
                                            xs: 12,
                                            sm: 12,
                                            md: 12,
                                            lg: 12,
                                        }}
                                    >
                                        <FormControl fullWidth>
                                            <TextField
                                                required
                                                id={CouponFormInput.TotalCouponCount}
                                                name={CouponFormInput.TotalCouponCount}
                                                label={t(
                                                    'campaignManagement.save.step.coupon.form.totalCouponCount.title'
                                                )}
                                                type="text"
                                                value={formatNumberWithDots(formValue?.totalCouponCount?.value || '')}
                                                defaultValue={formValue?.totalCouponCount?.value}
                                                disabled={!editable(CouponFormInput.TotalCouponCount)}
                                                onInput={handleCouponAmountChange}
                                                onChange={handleCouponAmountChange}
                                                error={errors.totalCouponCount != undefined}
                                                helperText={errors.totalCouponCount}
                                            />
                                        </FormControl>
                                    </Grid>
                                )}

                                {visible(CouponFormInput.UserFileId) && (
                                    <Grid
                                        style={{ paddingLeft: 0, margin: '8px' }}
                                        size={{
                                            xs: 12,
                                            sm: 12,
                                            md: 12,
                                            lg: 12,
                                        }}
                                    >
                                        {formData.couponType == CouponGenerationType.Personal && (
                                            <FormHelperText style={{ color: '#d32f2f' }}>
                                                {t('campaignManagement.save.step.coupon.form.file.alertMessage')}
                                            </FormHelperText>
                                        )}
                                        {editMode && (
                                            <Chip
                                                label={t('campaignManagement.save.step.coupon.form.file.downloadFile')}
                                                size="medium"
                                                variant="outlined"
                                                avatar={<img src={excelLogo} />}
                                                style={{ marginRight: '8px', width: '100%' }}
                                                onClick={() => downloadCouponFile(formData.id)}
                                                onDelete={() => downloadCouponFile(formData.id)}
                                                deleteIcon={<CloudDownload />}
                                            />
                                        )}
                                        {!editMode && <FileUpload />}
                                    </Grid>
                                )}

                                {visible(CouponFormInput.Status) && (
                                    <Grid
                                        style={{ paddingLeft: 0, margin: '8px' }}
                                        size={{
                                            xs: 12,
                                            sm: 12,
                                            md: 12,
                                            lg: 12,
                                        }}
                                    >
                                        <FormControl component="fieldset">
                                            <FormGroup aria-label="position" row>
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            color="primary"
                                                            name={CouponFormInput.Status}
                                                            defaultChecked={formValue.status.value == 1}
                                                        />
                                                    }
                                                    label={t('campaignManagement.save.step.coupon.form.active.title')}
                                                    name={CouponFormInput.Status}
                                                    labelPlacement="start"
                                                    style={{ margin: '0' }}
                                                    defaultChecked={formValue.status.value == 1}
                                                    disabled={!editable(CouponFormInput.Status)}
                                                    onChange={handleChange}
                                                />
                                            </FormGroup>
                                        </FormControl>
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Stack direction="row">
                            <Item style={{ padding: '16px 10px 16px 16px' }}>
                                <SCUIButton
                                    text={t('campaignManagement.save.step.coupon.modal.action.cancelButton')}
                                    type="secondary"
                                    sizeType="icon"
                                    size={'s'}
                                    onClick={onClose}
                                />
                            </Item>
                            <Item style={{ padding: '16px 16px 16px 10px' }}>
                                <SCUIButton
                                    autoFocusButton={true}
                                    text={t('campaignManagement.save.step.coupon.modal.action.saveButton')}
                                    type="primary"
                                    sizeType="fixed"
                                    size={'s'}
                                    onClick={() => handleSubmit(manualEvent)}
                                />
                            </Item>
                        </Stack>
                    </DialogActions>
                </>
            );
        });

        const BootstrapDialog = styled(Dialog)(({ theme }) => ({
            '& .MuiDialogContent-root': {
                padding: theme.spacing(0),
            },
            '& .MuiDialogActions-root': {
                padding: theme.spacing(0),
            },
            '& .MuiGrid-item': {
                paddingTop: `${theme.spacing(0)}!important`,
            },
        }));

        return (
            <BootstrapDialog onClose={onClose} aria-labelledby="coupon-modal" open={isOpen} maxWidth={'sm'} fullWidth>
                <DialogTitle sx={{ m: 1, p: 1 }} id="coupon-dialog-title" style={{ border: 0 }}>
                    <Typography variant="h6" gutterBottom style={{ fontSize: '2rem' }}>
                        {modalInfo.editMode
                            ? t('campaignManagement.save.step.coupon.modal.editTitle')
                            : t('campaignManagement.save.step.coupon.modal.createTitle')}
                    </Typography>
                    <Typography variant="subtitle1">
                        {t('campaignManagement.save.step.coupon.modal.description')}
                    </Typography>
                </DialogTitle>
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <Close />
                </IconButton>
                <div className="coupon-form">
                    <CouponForm />
                </div>
            </BootstrapDialog>
        );
    });

    const manualEvent = {
        preventDefault: () => {
            console.log('preventDefault is called manually');
        },
    };

    return (
        <Card className="step-form-card">
            <form
                id="StepCoupon"
                name="StepCoupon"
                noValidate={false}
                // onSubmit={stepperOnClickNext}
            >
                <CardHeader
                    className="step-form-card-header"
                    avatar={
                        <Avatar sx={{ bgcolor: blue[50], color: blue['A700'] }} variant="rounded">
                            <ConfirmationNumber sx={{ height: 24, width: 24 }} />
                        </Avatar>
                    }
                    title={t('campaignManagement.save.step.coupon.title')}
                    subheader={t('campaignManagement.save.step.coupon.description')}
                />

                <CardContent className="step-form-card-content">
                    <CouponCreateInputs />
                    <CouponList />
                </CardContent>

                <CardActions sx={{ m: 1 }} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Stack direction="row">
                        <Item>
                            <SCUIButton
                                text={t('campaignManagement.save.step.action.backButton')}
                                type="secondary"
                                sizeType="icon"
                                size={'s'}
                                buttonType="button"
                                onClick={stepperOnClickReset}
                            />
                        </Item>
                        <Item>
                            <SCUIButton
                                text={t('campaignManagement.save.step.action.saveButton')}
                                type="secondary"
                                sizeType="fixed"
                                size={'s'}
                                buttonType="button"
                                onClick={() => saveCampaignForm({})}
                                disabled={ContinueButtonDisabled}
                            />
                        </Item>
                        <Item>
                            <SCUIButton
                                text={t('campaignManagement.save.step.action.saveAndContinueButton')}
                                type="primary"
                                sizeType="fixed"
                                size={'s'}
                                buttonType="button"
                                onClick={saveForm}
                                disabled={ContinueButtonDisabled}
                            />
                        </Item>
                    </Stack>
                </CardActions>

                {modalInfo.open && <CouponModal {...props} isOpen={modalInfo.open} onClose={couponModalClose} />}
            </form>
        </Card>
    );
};
